/**
 * The Strategy Report viewer (#6232 Phase 3): the one living report,
 * rendered wherever /reports mounts it.
 *
 * The client's toolset: Request changes (a section rewrite), Edit
 * (WYSIWYG) and Export (a PDF matching the screen). Snapshots, versions and restore stay on the
 * dashboard for the agency. The needs-confirmation bar is the agency's, on the dashboard; the
 * client never sees the review workflow. No approval, no archive: the report
 * is updated, never signed off.
 */

import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Pencil,
  Save,
  AlertCircle,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlaybookRenderer } from '@/components/business/strategy-doc-playbook';
import { StrategyDocEditor } from '@/components/business/strategy-doc-editor';
import { splitCover } from '@/lib/strategy/report-cover';
import {
  getStrategyDocMarkdown,
  regenerateStrategyDocSection,
  saveStrategyDocMarkdown,
  type StrategyDocRunSummary,
} from '@/lib/strategy/api';

/**
 * Calls tab — the call-driven production pipeline:
 *   1. Upload call transcripts.
 *   2. Generate a strategy doc from the selected transcripts.
 *   3. Build the upload-ready build sheet from a generated doc.
 *
 * The generated documents themselves are read + approved in the Context tab
 * (see {@link StrategyContextPanel}). Only ``source === 'generated'`` runs show
 * here; uploaded docs are not part of the call pipeline.
 */
export function StrategyReportViewer({
  orgId,
  report,
  actionsContainer,
}: {
  orgId: number;
  report: StrategyDocRunSummary;
  /** When set, the toolbar buttons portal into this element (the tab bar's
   * actions slot) instead of rendering a row inside the card. */
  actionsContainer?: HTMLElement | null;
}) {
  const runId = report.id;
  const queryClient = useQueryClient();
  // `null` draft = view mode; a string = editing (the working copy the editor
  // reports back on every change). Reset whenever the open run changes so one
  // run's edits never bleed into another's view.
  const [draft, setDraft] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const isEditing = draft !== null;

  const markdownQuery = useQuery({
    queryKey: ['strategy', orgId, 'doc-run-md', runId],
    queryFn: () => getStrategyDocMarkdown(orgId, runId as number),
    enabled: runId !== null,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // Leaving edit mode on run-switch/close avoids a stale draft showing up
    // against a different document.
    setDraft(null);
  }, [runId]);

  const saveMutation = useMutation({
    mutationFn: (markdown: string) => saveStrategyDocMarkdown(orgId, runId as number, markdown),
    onSuccess: (savedMarkdown) => {
      // Seed the cache from the authoritative saved copy so the read-only
      // view reflects the edit without a refetch, then invalidate the run
      // list (updated_at changed).
      queryClient.setQueryData(['strategy', orgId, 'doc-run-md', runId], savedMarkdown);
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      setDraft(null);
    },
  });

  const loaded = !markdownQuery.isLoading && !markdownQuery.isError;

  // Export (#6671): the same jsPDF stack as the other reports, themed to the
  // playbook's screen rendering (cover band, hero and quote boxes, section
  // rules), saved straight to a file. No print dialog.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportPdf = async () => {
    const content = draft ?? markdownQuery.data;
    if (!content) return;
    setExporting(true);
    setExportError(null);
    try {
      const { generateMarkdownPDF } = await import('@/lib/pdf');
      const { body, title, dateLabel } = splitCover(content);
      const blob = await generateMarkdownPDF({
        content: body,
        headerLabel: 'Strategy Report',
        cover: { title, dateLabel },
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'strategy-report.pdf';
      // A detached anchor's click() is silently ignored by some browsers.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'The PDF could not be made. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className='rounded-lg border bg-white shadow-sm overflow-hidden'>
        {markdownQuery.isLoading ? (
          <div className='flex items-center gap-2 p-6 text-sm text-gray-500'>
            <Spinner size="sm" />
            Loading document...
          </div>
        ) : markdownQuery.isError ? (
          <div className='p-6 text-sm text-[#B91C1C]'>
            {(markdownQuery.error as Error).message}
          </div>
        ) : (
          <>
            {/*
              Toolbar (#6232): the buttons live OUTSIDE the card, portalled
              into the tab bar's actions slot when the host provides one
              (falls back to a quiet in-card row otherwise). No metadata row:
              the document's own cover announces the title.
            */}
            {(() => {
              const toolbar = (
              <div className='flex items-center gap-2'>
                {isEditing ? (
                  <>
                    {saveMutation.isError ? (
                      <span className='flex items-center gap-1 text-xs text-[#B91C1C]'>
                        <AlertCircle className='h-3.5 w-3.5' />
                        {(saveMutation.error as Error).message}
                      </span>
                    ) : null}
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-9'
                      disabled={saveMutation.isPending}
                      onClick={() => setDraft(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='sm'
                      className='h-9 bg-[#290BD9] hover:bg-[#2309C5]'
                      disabled={saveMutation.isPending || draft.trim().length === 0}
                      onClick={() => saveMutation.mutate(draft)}
                    >
                      {saveMutation.isPending ? <Spinner size='sm' className='mr-2' /> : <Save className='mr-2 h-4 w-4' />}
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    {exportError && (
                      <span className='flex items-center gap-1 text-xs text-[#B91C1C]'>
                        <AlertCircle className='h-3.5 w-3.5' />
                        {exportError}
                      </span>
                    )}
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-9'
                      disabled={!loaded}
                      onClick={() => setRegenOpen(true)}
                    >
                      <Sparkles className='mr-2 h-4 w-4' />
                      Request changes
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-9'
                      disabled={!loaded}
                      onClick={() => setDraft(markdownQuery.data ?? '')}
                    >
                      <Pencil className='mr-2 h-4 w-4' />
                      Edit
                    </Button>
                    <Button variant='outline' size='sm' className='h-9' disabled={!loaded || exporting} onClick={exportPdf}>
                      {exporting ? <Spinner size='sm' className='mr-2' /> : <Download className='mr-2 h-4 w-4' />}
                      Export
                    </Button>
                  </>
                )}
              </div>
              );
              return actionsContainer ? (
                createPortal(toolbar, actionsContainer)
              ) : (
                <div className='flex items-center justify-end border-b border-gray-200 bg-white px-6 py-1.5' data-print-hidden>
                  {toolbar}
                </div>
              );
            })()}
            {isEditing ? (
              <StrategyDocEditor
                initialMarkdown={markdownQuery.data ?? ''}
                onChange={setDraft}
              />
            ) : (
              <>
                <PlaybookRenderer content={markdownQuery.data ?? ''} />
              </>
            )}
            <RegenerateSectionDialog
              open={regenOpen}
              onClose={() => setRegenOpen(false)}
              orgId={orgId}
              runId={runId}
              markdown={markdownQuery.data ?? ''}
            />
          </>
        )}
    </div>
  );
}

/** The user-steered rewrite (#6085 Phase 3): pick one section, say what should
 * change, and that section alone is regenerated grounded in the run's inputs.
 * Distinct from the internal judge/self-correction loop. */
function RegenerateSectionDialog({
  open,
  onClose,
  orgId,
  runId,
  markdown,
}: {
  open: boolean;
  onClose: () => void;
  orgId: number;
  runId: number | null;
  markdown: string;
}) {
  const queryClient = useQueryClient();
  const [heading, setHeading] = useState<string>('');
  const [feedback, setFeedback] = useState('');
  const headings = markdown.match(/^## .*$/gm) ?? [];

  useEffect(() => {
    if (open) {
      setHeading('');
      setFeedback('');
    }
  }, [open]);

  const regenMutation = useMutation({
    mutationFn: () => regenerateStrategyDocSection(orgId, runId as number, heading, feedback.trim()),
    onSuccess: (updatedMarkdown) => {
      queryClient.setQueryData(['strategy', orgId, 'doc-run-md', runId], updatedMarkdown);
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>Request changes</DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <p className='text-sm text-gray-500'>
            Choose the part of the document to change and describe the change. That part is rewritten to your
            instruction, grounded in the agreed call brief and account data; the rest of the document is kept as is.
          </p>
          <div className='space-y-1.5'>
            <Label className='text-gray-900'>Section</Label>
            <Select value={heading} onValueChange={setHeading}>
              <SelectTrigger className='w-full text-gray-900'>
                <SelectValue placeholder='Choose a section' />
              </SelectTrigger>
              <SelectContent>
                {headings.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h.replace(/^## /, '')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label className='text-gray-900'>What should change</Label>
            <Textarea
              className='min-h-[100px]'
              maxLength={4000}
              placeholder='e.g. Make the launch timeline more aggressive, and lead with the CPA target.'
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
          {regenMutation.isError && (
            <div className='border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-3 text-sm flex items-center gap-2'>
              <AlertCircle className='h-4 w-4 flex-shrink-0' /> The section could not be regenerated. Please try again.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' className='h-9 text-gray-900' onClick={onClose} disabled={regenMutation.isPending}>
            Cancel
          </Button>
          <Button
            className='h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white'
            disabled={regenMutation.isPending || !heading || feedback.trim().length === 0}
            onClick={() => regenMutation.mutate()}
          >
            {regenMutation.isPending ? (
              <>
                <Spinner size='sm' className='mr-2' /> Regenerating...
              </>
            ) : (
              <>
                <Sparkles className='mr-2 h-4 w-4' /> Regenerate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}







/**
 * Separate "Corrections" document, opened by its own button on a run row. It
 * lists the doctrine deviations the self-correction loop caught and rewrote the
 * playbook to fix (each cited to a taxonomy node + handbook section), plus any
 * residual deviation the loop could not fully resolve within the pass cap. This
 * is deliberately NOT shown on the playbook itself: the customer doc stays
 * clean and this is the behind-the-scenes record of what was changed.
 */
/** The sheet's sections, in its own render order — ids match the renderer's
 * `<section id>` values, so the app-side nav can drive a sandboxed render
 * (the sheet's inline tab JS never runs inside the sandbox). */
